/**
 * FysiekePhaseTest — geïsoleerde testpagina voor PhysicalMeetingPhaseV2.
 *
 * GEBRUIK:
 *   1. Ga naar /#fysieke-test in je browser.
 *   2. Vul een bestaand connectionId in (bijv. uit Firestore).
 *   3. Klik "Laad connectie" → de component wordt gerenderd met jouw echte auth.
 *
 * Zo kun je de fysieke fase volledig testen los van ConnectieFlow.
 */

import React, { useEffect, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { auth, db } from '../firebase';
import PhysicalMeetingPhaseV2 from './connection-phases/PhysicalMeetingPhaseV2';

interface CurrentUser {
  uid: string;
  email: string | null;
  displayName: string | null;
}

interface ConnectionData {
  id: string;
  user1Id: string;
  user2Id: string;
  user1Name?: string;
  user2Name?: string;
  status?: string;
  phase?: string;
}

interface ConnectionListItem {
  id: string;
  otherName: string;
  status: string;
  phase: string;
}

const FysiekePhaseTest: React.FC = () => {
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);
  const [connectionIdInput, setConnectionIdInput] = useState('');
  const [connectionData, setConnectionData] = useState<ConnectionData | null>(null);
  const [loadError, setLoadError] = useState('');
  const [loading, setLoading] = useState(false);
  const [agendaLog, setAgendaLog] = useState<string[]>([]);
  const [myConnections, setMyConnections] = useState<ConnectionListItem[]>([]);
  const [loadingConnections, setLoadingConnections] = useState(false);
  const [uidCopied, setUidCopied] = useState(false);

  // Firebase auth
  useEffect(() => {
    return onAuthStateChanged(auth, (u) => {
      if (u) {
        setCurrentUser({ uid: u.uid, email: u.email, displayName: u.displayName });
      } else {
        setCurrentUser(null);
      }
    });
  }, []);

  // Haal alle connecties op voor de ingelogde user
  const fetchMyConnections = async () => {
    if (!currentUser) return;
    setLoadingConnections(true);
    setMyConnections([]);
    try {
      const [snap1, snap2] = await Promise.all([
        getDocs(query(collection(db, 'connections'), where('user1Id', '==', currentUser.uid))),
        getDocs(query(collection(db, 'connections'), where('user2Id', '==', currentUser.uid))),
      ]);

      const items: ConnectionListItem[] = [];
      [...snap1.docs, ...snap2.docs].forEach(d => {
        const data = d.data();
        const isUser1 = data.user1Id === currentUser.uid;
        const otherName = isUser1
          ? (data.user2Name || data.user2DisplayName || data.user2Id?.slice(0, 8) + '...')
          : (data.user1Name || data.user1DisplayName || data.user1Id?.slice(0, 8) + '...');
        items.push({
          id: d.id,
          otherName,
          status: data.status || '—',
          phase: data.phase || '—',
        });
      });

      // Sorteer: physical first, dan accepted
      items.sort((a, b) => {
        const order = ['physical', 'accepted', 'completed', 'pending'];
        return order.indexOf(a.status) - order.indexOf(b.status);
      });

      setMyConnections(items);
    } catch (err: unknown) {
      setLoadError(`Fout bij ophalen connecties: ${(err as Error).message}`);
    } finally {
      setLoadingConnections(false);
    }
  };

  const handleCopyUid = () => {
    if (!currentUser) return;
    navigator.clipboard.writeText(currentUser.uid).then(() => {
      setUidCopied(true);
      setTimeout(() => setUidCopied(false), 2000);
    });
  };

  const loadConnectionById = async (id: string) => {
    if (!id.trim()) {
      setLoadError('Vul een connectionId in.');
      return;
    }
    if (!currentUser) {
      setLoadError('Je bent niet ingelogd.');
      return;
    }

    setLoading(true);
    setLoadError('');
    setConnectionData(null);

    try {
      const snap = await getDoc(doc(db, 'connections', id.trim()));
      if (!snap.exists()) {
        setLoadError(`Geen connectie gevonden met id: ${id}`);
        return;
      }

      const data = snap.data();
      const conn: ConnectionData = {
        id: id.trim(),
        user1Id: data.user1Id || '',
        user2Id: data.user2Id || '',
        user1Name: data.user1Name || data.user1DisplayName || '',
        user2Name: data.user2Name || data.user2DisplayName || '',
        status: data.status,
        phase: data.phase,
      };

      if (conn.user1Id !== currentUser.uid && conn.user2Id !== currentUser.uid) {
        setLoadError(
          `Jij (${currentUser.uid.slice(0, 8)}...) bent geen deelnemer van deze connectie.\n` +
          `user1Id: ${conn.user1Id.slice(0, 8)}...\nuser2Id: ${conn.user2Id.slice(0, 8)}...`
        );
        return;
      }

      setConnectionData(conn);
    } catch (err: unknown) {
      setLoadError(`Fout bij ophalen: ${(err as Error).message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadConnection = () => loadConnectionById(connectionIdInput);

  const handleAgendaOpen = () => {
    const msg = `[${new Date().toLocaleTimeString('nl-NL')}] onOpenAgenda() aangeroepen`;
    setAgendaLog(prev => [msg, ...prev]);
    alert('📅 onOpenAgenda() werd aangeroepen.\n\nIn de echte app opent hier de ExtendedAgenda.\nDeze testpagina logt het alleen.');
  };

  // Bepaal huidige user vs. andere user
  const isUser1 = connectionData?.user1Id === currentUser?.uid;
  const myUserId = currentUser?.uid || '';
  const myUserName = currentUser?.displayName || currentUser?.email?.split('@')[0] || 'Jij';
  const otherUserId = connectionData
    ? (isUser1 ? connectionData.user2Id : connectionData.user1Id)
    : '';
  const otherUserName = connectionData
    ? (isUser1
        ? (connectionData.user2Name || 'Andere gebruiker')
        : (connectionData.user1Name || 'Andere gebruiker'))
    : '';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3 shadow-sm">
        <div className="text-xl">🧪</div>
        <div>
          <h1 className="text-base font-semibold text-gray-800">Fysieke Fase — Geïsoleerde Test</h1>
          <p className="text-xs text-gray-500">
            Test <code>PhysicalMeetingPhaseV2</code> volledig los van ConnectieFlow
          </p>
        </div>
      </div>

      {/* Setup panel — alleen tonen als er nog geen connectie geladen is */}
      {!connectionData && (
        <div className="max-w-xl mx-auto mt-8 px-4 pb-12">
          {/* Auth status */}
          <div
            className={`rounded-lg px-4 py-3 mb-4 text-sm ${
              currentUser
                ? 'bg-green-50 border border-green-200 text-green-800'
                : 'bg-red-50 border border-red-200 text-red-800'
            }`}
          >
            {currentUser ? (
              <div className="flex items-center justify-between gap-2">
                <span>✅ <strong>{currentUser.email}</strong></span>
                <button
                  onClick={handleCopyUid}
                  className="text-xs bg-white border border-green-300 text-green-700 rounded px-2 py-0.5 hover:bg-green-50 transition-colors"
                >
                  {uidCopied ? '✓ Gekopieerd!' : `uid: ${currentUser.uid.slice(0, 10)}… kopieer`}
                </button>
              </div>
            ) : '❌ Niet ingelogd — log eerst in via de normale app'}
          </div>

          {/* Mijn connecties ophalen */}
          {currentUser && (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 mb-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold text-gray-700">Mijn connecties</h2>
                <button
                  onClick={fetchMyConnections}
                  disabled={loadingConnections}
                  className="text-xs bg-[#7A9488] hover:bg-[#4F6660] disabled:opacity-50 text-white rounded-lg px-3 py-1.5 font-medium transition-colors"
                >
                  {loadingConnections ? 'Laden...' : '🔍 Haal op uit Firestore'}
                </button>
              </div>

              {myConnections.length === 0 && !loadingConnections && (
                <p className="text-xs text-gray-400">Klik op "Haal op" om je connecties te zien.</p>
              )}

              {myConnections.length > 0 && (
                <div className="space-y-2">
                  {myConnections.map(conn => (
                    <button
                      key={conn.id}
                      onClick={() => {
                        setConnectionIdInput(conn.id);
                        loadConnectionById(conn.id);
                      }}
                      className="w-full text-left flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2.5 hover:border-[#7A9488] hover:bg-[#f0f5f3] transition-colors group"
                    >
                      <div>
                        <div className="text-sm font-medium text-gray-800 group-hover:text-[#4F6660]">
                          {conn.otherName}
                        </div>
                        <div className="text-xs text-gray-400 font-mono">{conn.id}</div>
                      </div>
                      <div className="flex gap-1.5 items-center shrink-0">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          conn.phase === 'physical' ? 'bg-purple-100 text-purple-700'
                          : conn.status === 'accepted' ? 'bg-green-100 text-green-700'
                          : conn.status === 'completed' ? 'bg-gray-100 text-gray-500'
                          : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {conn.phase !== '—' ? conn.phase : conn.status}
                        </span>
                        <span className="text-[#7A9488] text-xs">→</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Of handmatig ID invoeren */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <h2 className="text-sm font-semibold text-gray-700 mb-3">
              {myConnections.length > 0 ? 'Of voer handmatig een ID in' : 'Laad een connectie'}
            </h2>

            <div className="mb-1 text-xs text-gray-500">Connection ID</div>
            <input
              type="text"
              value={connectionIdInput}
              onChange={e => setConnectionIdInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleLoadConnection()}
              placeholder="bijv. 54WEKAWvDLKKmMumgTB8"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#7A9488] mb-3"
            />

            <button
              onClick={handleLoadConnection}
              disabled={loading || !currentUser || !connectionIdInput.trim()}
              className="w-full bg-[#7A9488] hover:bg-[#4F6660] disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold text-sm rounded-lg py-2.5 transition-colors"
            >
              {loading ? 'Laden...' : '→ Laad connectie & start test'}
            </button>

            {loadError && (
              <div className="mt-3 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-xs text-red-700 whitespace-pre-wrap">
                {loadError}
              </div>
            )}

            <div className="mt-4 text-xs text-gray-400 border-t pt-3">
              <strong>Tip:</strong> Open in twee tabbladen als twee verschillende users voor volledige realtime-samenwerking.
            </div>
          </div>
        </div>
      )}

      {/* Component volledig geïsoleerd renderen */}
      {connectionData && currentUser && (
        <div>
          {/* Info-balk bovenin */}
          <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 text-xs text-amber-800 flex flex-wrap items-center gap-4">
            <span>
              <strong>Connectie:</strong> <code>{connectionData.id}</code>
            </span>
            <span>
              <strong>Jij:</strong> {myUserName} ({isUser1 ? 'user1' : 'user2'})
            </span>
            <span>
              <strong>Ander:</strong> {otherUserName}
            </span>
            <span>
              <strong>Status:</strong> {connectionData.status || '—'} /
              <strong> Fase:</strong> {connectionData.phase || '—'}
            </span>
            <button
              onClick={() => setConnectionData(null)}
              className="ml-auto text-amber-700 underline hover:no-underline"
            >
              ✕ Andere connectie laden
            </button>
          </div>

          {/* onOpenAgenda log */}
          {agendaLog.length > 0 && (
            <div className="bg-blue-50 border-b border-blue-200 px-4 py-1.5 text-xs text-blue-700">
              <strong>Agenda callbacks:</strong>{' '}
              {agendaLog.slice(0, 3).join(' · ')}
            </div>
          )}

          {/* DE COMPONENT ZELF */}
          <PhysicalMeetingPhaseV2
            connectionId={connectionData.id}
            currentUserId={myUserId}
            currentUserName={myUserName}
            otherUserId={otherUserId}
            otherUserName={otherUserName}
            currentUserProfile={undefined}
            otherUserProfile={undefined}
            onOpenAgenda={handleAgendaOpen}
          />
        </div>
      )}
    </div>
  );
};

export default FysiekePhaseTest;
